import { TScoreSkillLevel } from '@/types';
import { capitalize } from 'lodash';
import { FEATURE_FLAGS } from './feature-flags';
import { SKILL_LEVEL_LABELS, SKILL_LEVELS } from './labels';

/**
 * Immutable IDs for skill levels, decoupled from display labels for
 * flexibility in terminology changes. Each ID maps to a canonical skill level
 * code and a display label.
 */
export type TSkillLevelId = 'L1' | 'L2' | 'L3' | 'L4';

/**
 * Centralized definitions and utilities for skill levels in the application.
 */
type TSkillLevelDefinition = {
  code: TScoreSkillLevel;
  label: string;
  legacyAliases: readonly string[];
};

/**
 * Flag-aware display labels for stable score levels.
 *
 * - v2 enabled: prefer canonical naming (`Awareness`, `Skilled`)
 * - v2 disabled: prefer legacy naming (`Beginner`, `Intermediate`)
 */
const SCORECARD_LEVEL_LABELS = {
  L1: capitalize(
    FEATURE_FLAGS.ENABLE_SCORECARD_V2 ? SKILL_LEVELS.AWARENESS : SKILL_LEVELS.BEGINNER,
  ),
  L2: capitalize(
    FEATURE_FLAGS.ENABLE_SCORECARD_V2 ? SKILL_LEVELS.SKILLED : SKILL_LEVELS.INTERMEDIATE,
  ),
  L3: SKILL_LEVEL_LABELS.L3,
  L4: SKILL_LEVEL_LABELS.L4,
} as const;

/**
 * Central registry for skill level definitions, keyed by immutable IDs (L1-L4)
 * for fluidity when terminology changes. Each level maps to a canonical code
 * and display label.
 */
export const SKILL_LEVEL_DEFINITIONS = {
  L1: {
    code: SKILL_LEVELS.AWARENESS,
    label: SCORECARD_LEVEL_LABELS.L1,
    legacyAliases: [SKILL_LEVELS.BEGINNER, SKILL_LEVELS.NOVICE],
  },
  L2: {
    code: SKILL_LEVELS.SKILLED,
    label: SCORECARD_LEVEL_LABELS.L2,
    legacyAliases: [SKILL_LEVELS.INTERMEDIATE],
  },
  L3: {
    code: SKILL_LEVELS.ADVANCED,
    label: SKILL_LEVEL_LABELS.L3,
    legacyAliases: [],
  },
  L4: {
    code: SKILL_LEVELS.EXPERT,
    label: SKILL_LEVEL_LABELS.L4,
    legacyAliases: [],
  },
} as const satisfies Record<TSkillLevelId, TSkillLevelDefinition>;

/**
 * Reverse lookup: maps canonical skill level codes to their immutable IDs
 */
export const SKILL_LEVEL_BY_CODE: Record<TScoreSkillLevel, TSkillLevelId> = {
  [SKILL_LEVELS.AWARENESS]: 'L1',
  [SKILL_LEVELS.BEGINNER]: 'L1', // Legacy alias
  [SKILL_LEVELS.SKILLED]: 'L2',
  [SKILL_LEVELS.INTERMEDIATE]: 'L2', // Legacy alias
  [SKILL_LEVELS.ADVANCED]: 'L3',
  [SKILL_LEVELS.EXPERT]: 'L4',
  [SKILL_LEVELS.NOVICE]: 'L1', // Legacy alias
} as const;

/**
 * Ordered list of skill level IDs
 */
export const SKILL_LEVEL_ORDER = [
  'L1',
  'L2',
  'L3',
  'L4',
] as const satisfies readonly TSkillLevelId[];

/**
 * Exported enum of skill level labels for use in UI components,
 * mapped from definitions to ensure consistency and maintainability
 * if labels change.
 */
export const ESkillLevel = {
  L1: SKILL_LEVEL_DEFINITIONS.L1.label,
  L2: SKILL_LEVEL_DEFINITIONS.L2.label,
  L3: SKILL_LEVEL_DEFINITIONS.L3.label,
  L4: SKILL_LEVEL_DEFINITIONS.L4.label,
} as const;

/**
 * Type for skill level labels, derived from the ESkillLevel enum
 * to ensure type safety and consistency across the app.
 */
export type ESkillLevel = (typeof ESkillLevel)[keyof typeof ESkillLevel];

/**
 * Maps skill level codes to their display labels
 */
export const RESOLVED_SKILL_LEVEL_LABELS: Record<TScoreSkillLevel, ESkillLevel> = {
  [SKILL_LEVELS.AWARENESS]: ESkillLevel.L1,
  [SKILL_LEVELS.BEGINNER]: ESkillLevel.L1,
  [SKILL_LEVELS.SKILLED]: ESkillLevel.L2,
  [SKILL_LEVELS.INTERMEDIATE]: ESkillLevel.L2,
  [SKILL_LEVELS.ADVANCED]: ESkillLevel.L3,
  [SKILL_LEVELS.EXPERT]: ESkillLevel.L4,
  [SKILL_LEVELS.NOVICE]: ESkillLevel.L1,
};
